import React, { useMemo, useRef, useState } from "react";

import {
  ArrowDownUp,
  Edit,
  EyeOff,
  GripVertical,
  Layers,
  Plus,
  Power,
  RotateCcw,
  Save,
  SearchX,
  Trash2,
} from "lucide-react";
import { createPortal } from "react-dom";

import EmptyState from "./EmptyState";
import FilterChips from "./FilterChips";
import ItemActionsMenu, { type ItemAction } from "./ItemActionsMenu";
import LayoutToggle, { VideoLayout } from "./LayoutToggle";
import ReorderControls from "./ReorderControls";
import { runSmoothReorder, useReorderHighlight } from "./reorderMotion";
import { moveItemInArray, type ReorderDirection } from "./reorderUtils";
import SortableTopicGrid from "./SortableTopicGrid";
import WorkspaceSearch from "./WorkspaceSearch";
import Button from "../../Components/Button";
import CommonInput from "../../Components/Input";
import CommonModal from "../../Components/Modal";
import TruncatedTooltip from "../../Components/TruncatedTooltip";
import DeleteModal from "../../Container/DeleteModal";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  createVideoTopic,
  deleteVideoTopic,
  renameVideoTopic,
  reorderVideoTopics,
  setVideoTopicActive,
  VideoTopic,
} from "../../Store/Reducers/VideoSlice";

const SUGGESTED_TOPICS = ["Overviews", "Playbooks", "How-to videos"];
const ALL_STATUS = "all";
const ACTIVE_STATUS = "active";
const INACTIVE_STATUS = "inactive";

const topicIds = (topics: VideoTopic[]) =>
  topics.map(topic => topic.id).join("|");

type TopicFormState =
  | { mode: "create"; label: string }
  | { mode: "edit"; topicId: string; label: string };

interface TopicsPanelProps {
  actionsSlot: HTMLElement | null;
}

const TopicsPanel: React.FC<TopicsPanelProps> = ({ actionsSlot }) => {
  const dispatch = useAppDispatch();
  const {
    topics: topicList,
    topicsLoading,
    librarySaving: saving,
  } = useAppSelector(state => state.video);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(ALL_STATUS);
  const [layout, setLayout] = useState<VideoLayout>("cards");
  const [topicForm, setTopicForm] = useState<TopicFormState | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<VideoTopic | null>(null);
  const [statusTarget, setStatusTarget] = useState<{
    topic: VideoTopic;
    toActive: boolean;
  } | null>(null);
  const [editTopics, setEditTopics] = useState<VideoTopic[] | null>(null);
  const [orderSaving, setOrderSaving] = useState(false);

  const canvasRef = useRef<HTMLDivElement>(null);
  const listReorderRef = useRef<HTMLDivElement | null>(null);
  const { highlightId, flash, isHighlighted } = useReorderHighlight();
  const isEditing = editTopics !== null;
  const query = search.trim().toLowerCase();
  const activeCount = topicList.filter(
    topic => topic.is_active !== false
  ).length;
  const inactiveCount = topicList.length - activeCount;

  const filterChips = [
    { id: ALL_STATUS, label: "All", count: topicList.length },
    { id: ACTIVE_STATUS, label: "Active", count: activeCount },
    { id: INACTIVE_STATUS, label: "Inactive", count: inactiveCount },
  ];

  const visibleTopics = useMemo(() => {
    const filtered = topicList.filter(topic => {
      const matchesSearch = !query || topic.label.toLowerCase().includes(query);
      const matchesStatus =
        statusFilter === ALL_STATUS ||
        (statusFilter === ACTIVE_STATUS
          ? topic.is_active !== false
          : topic.is_active === false);
      return matchesSearch && matchesStatus;
    });
    // Browse "All": inactive first, then active (catalog order within each group).
    if (statusFilter !== ALL_STATUS) return filtered;
    return [...filtered].sort((a, b) => {
      const aInactive = a.is_active === false ? 0 : 1;
      const bInactive = b.is_active === false ? 0 : 1;
      if (aInactive !== bInactive) return aInactive - bInactive;
      return (
        (a.sort_order || 0) - (b.sort_order || 0) ||
        a.label.localeCompare(b.label)
      );
    });
  }, [topicList, query, statusFilter]);

  const hasOrderChanges =
    isEditing && topicIds(editTopics) !== topicIds(topicList);

  /** Active topics only — inactive cards stay visible but are skipped in 1…N. */
  const activeDisplayOrder = useMemo(() => {
    const map = new Map<string, number>();
    let order = 0;
    topicList.forEach(topic => {
      if (topic.is_active === false) return;
      order += 1;
      map.set(topic.id, order);
    });
    return map;
  }, [topicList]);

  const exitEditing = () => setEditTopics(null);

  const closeTopicForm = () => setTopicForm(null);

  const openCreateTopic = () => setTopicForm({ mode: "create", label: "" });

  const openEditTopic = (topic: VideoTopic) =>
    setTopicForm({ mode: "edit", topicId: topic.id, label: topic.label });

  const setTopicFormLabel = (label: string) =>
    setTopicForm(prev => (prev ? { ...prev, label } : prev));

  const handleSubmitTopicForm = async () => {
    if (!topicForm) return;
    const label = topicForm.label.trim();
    if (!label) return;

    if (topicForm.mode === "create") {
      const result = await dispatch(createVideoTopic(label));
      if (createVideoTopic.fulfilled.match(result)) closeTopicForm();
      return;
    }

    const result = await dispatch(
      renameVideoTopic({ id: topicForm.topicId, label })
    );
    if (renameVideoTopic.fulfilled.match(result)) closeTopicForm();
  };

  const handleCreateSuggested = async () => {
    for (const label of SUGGESTED_TOPICS) {
      await dispatch(createVideoTopic(label));
    }
  };

  const handleListReorder = (index: number, direction: ReorderDirection) => {
    const movedId = editTopics?.[index]?.id;
    runSmoothReorder(listReorderRef.current, () => {
      setEditTopics(prev => {
        if (!prev) return prev;
        return moveItemInArray(prev, index, direction).map(
          (topic, orderIndex) => ({
            ...topic,
            sort_order: orderIndex + 1,
          })
        );
      });
    });
    if (movedId) flash(movedId);
  };

  const handleSaveOrder = async () => {
    if (!editTopics || !hasOrderChanges) return;
    setOrderSaving(true);
    try {
      const result = await dispatch(
        reorderVideoTopics(editTopics.map(topic => topic.id))
      );
      if (reorderVideoTopics.fulfilled.match(result)) exitEditing();
    } finally {
      setOrderSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const result = await dispatch(deleteVideoTopic(deleteTarget.id));
    if (deleteVideoTopic.fulfilled.match(result)) setDeleteTarget(null);
  };

  const handleConfirmStatus = async () => {
    if (!statusTarget) return;
    const result = await dispatch(
      setVideoTopicActive({
        id: statusTarget.topic.id,
        isActive: statusTarget.toActive,
      })
    );
    if (setVideoTopicActive.fulfilled.match(result)) setStatusTarget(null);
  };

  const topicActions = (topic: VideoTopic): ItemAction[] => [
    {
      id: "status",
      label: topic.is_active === false ? "Activate" : "Deactivate",
      icon: topic.is_active === false ? Power : EyeOff,
      disabled: saving,
      onClick: () =>
        setStatusTarget({
          topic,
          toActive: topic.is_active === false,
        }),
    },
    {
      id: "rename",
      label: "Rename",
      icon: Edit,
      disabled: saving,
      onClick: () => openEditTopic(topic),
    },
    {
      id: "delete",
      label: "Delete topic",
      icon: Trash2,
      danger: true,
      disabled: saving,
      onClick: () => setDeleteTarget(topic),
    },
  ];

  const renderList = (topics: VideoTopic[], showReorder: boolean) => (
    <div
      className="topic-list"
      role="list"
      ref={showReorder ? listReorderRef : undefined}
    >
      {topics.map((topic, index) => {
        const position = showReorder
          ? index + 1
          : (activeDisplayOrder.get(topic.id) ?? null);
        return (
          <div
            key={topic.id}
            data-reorder-id={showReorder ? topic.id : undefined}
            className={`topic-row${
              topic.is_active === false ? " topic-row--inactive" : ""
            }${showReorder ? " topic-row--reorder" : ""}${
              showReorder && isHighlighted(topic.id)
                ? " is-reorder-highlight"
                : ""
            }`}
            role="listitem"
          >
            <span
              className={`topic-row__position${
                position == null ? " topic-row__position--muted" : ""
              }`}
            >
              {position ?? "—"}
            </span>
            <div className="flex-1 min-w-0">
              <p className="topic-row__label">
                {topic.label}
                {topic.is_active === false && (
                  <span className="topic-row__status">Hidden from users</span>
                )}
              </p>
            </div>
            <span className="topic-row__count">
              {topic.video_count} {topic.video_count === 1 ? "video" : "videos"}
            </span>
            <span
              className={`topic-row__status-pill${
                topic.is_active === false ? " topic-row__status-pill--off" : ""
              }`}
            >
              {topic.is_active === false ? "Inactive" : "Active"}
            </span>
            {showReorder ? (
              <ReorderControls
                index={index}
                length={topics.length}
                disabled={orderSaving || saving}
                onMove={direction => handleListReorder(index, direction)}
              />
            ) : (
              <div className="topic-row__actions">
                <ItemActionsMenu
                  ariaLabel="Topic actions"
                  actions={topicActions(topic)}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderCards = () => (
    <div className="topic-card-grid">
      {visibleTopics.map(topic => {
        const position = activeDisplayOrder.get(topic.id) ?? null;
        return (
          <article
            key={topic.id}
            className={`topic-card${
              topic.is_active === false ? " topic-card--inactive" : ""
            }`}
          >
            <div className="topic-card__top">
              <span
                className={`topic-row__position${
                  position == null ? " topic-row__position--muted" : ""
                }`}
              >
                {position ?? "—"}
              </span>
              <div className="topic-card__top-end">
                <span
                  className={`topic-row__status-pill${
                    topic.is_active === false
                      ? " topic-row__status-pill--off"
                      : ""
                  }`}
                >
                  {topic.is_active === false ? "Inactive" : "Active"}
                </span>
                <div className="topic-row__actions">
                  <ItemActionsMenu
                    ariaLabel="Topic actions"
                    actions={topicActions(topic)}
                  />
                </div>
              </div>
            </div>

            <div className="topic-card__body">
              <TruncatedTooltip
                tooltip={topic.label}
                className="topic-card__title"
              >
                {topic.label}
              </TruncatedTooltip>
              <div className="topic-card__meta">
                <p className="topic-card__count">
                  {topic.video_count}{" "}
                  {topic.video_count === 1 ? "video" : "videos"}
                </p>
                {topic.is_active === false ? (
                  <span className="topic-row__status">Hidden from users</span>
                ) : null}
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );

  const renderCanvas = () => {
    if (topicsLoading && topicList.length === 0) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-200 border-t-purple-600" />
        </div>
      );
    }

    if (isEditing) {
      const isListEdit = layout === "list";
      return (
        <>
          <div className="reorder-banner" role="note">
            <span className="reorder-banner__icon">
              {isListEdit ? (
                <ArrowDownUp size={16} />
              ) : (
                <GripVertical size={16} />
              )}
            </span>
            <div>
              <p className="reorder-banner__title">
                {isListEdit
                  ? "Use move actions to set topic order"
                  : "Drag cards to set topic order"}
              </p>
              <p className="reorder-banner__text">
                This controls how topics appear for users. Click Save Order when
                you&apos;re done.{" "}
                <span className="reorder-banner__tip">
                  {isListEdit
                    ? "To drag and drop instead, discard, switch to card view, then click Edit Order."
                    : "To use move up/down controls instead, discard, switch to list view, then click Edit Order."}
                </span>
              </p>
            </div>
          </div>
          {isListEdit ? (
            renderList(editTopics, true)
          ) : (
            <SortableTopicGrid
              topics={editTopics}
              highlightId={highlightId}
              onMoved={flash}
              onTopicsChange={setEditTopics}
            />
          )}
        </>
      );
    }

    if (topicList.length === 0) {
      return (
        <EmptyState
          icon={Layers}
          title="No topics yet"
          description="Without topics, users see a single video list as today."
        >
          <button
            type="button"
            className="topics-empty__suggest"
            disabled={saving}
            onClick={handleCreateSuggested}
          >
            <Plus size={14} />
            Use suggested topics
          </button>
          <span className="text-xs text-gray-400">
            {SUGGESTED_TOPICS.join(" · ")}
          </span>
        </EmptyState>
      );
    }

    if (visibleTopics.length === 0) {
      return (
        <EmptyState
          icon={SearchX}
          title={
            query ? (
              <>No topics match &quot;{search.trim()}&quot;</>
            ) : statusFilter === ACTIVE_STATUS ? (
              "No active topics"
            ) : (
              "No inactive topics"
            )
          }
          description={
            query
              ? "Try a different search term."
              : "Switch the status filter to see other topics."
          }
        />
      );
    }

    return layout === "list" ? renderList(visibleTopics, false) : renderCards();
  };

  const isEditMode = topicForm?.mode === "edit";

  return (
    <div className="admin-video-workspace__panel">
      {actionsSlot &&
        createPortal(
          <>
            <WorkspaceSearch
              value={search}
              onChange={setSearch}
              placeholder="Search topics…"
              ariaLabel="Search topics"
              disabled={isEditing}
            />
            {isEditing ? (
              <>
                <Button
                  variant="secondary"
                  icon={<RotateCcw size={18} />}
                  onClick={exitEditing}
                >
                  Discard
                </Button>
                <Button
                  variant="primary"
                  icon={<Save size={18} />}
                  loading={orderSaving || saving}
                  disabled={!hasOrderChanges}
                  onClick={handleSaveOrder}
                >
                  Save Order
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="secondary"
                  disabled={topicList.length < 2}
                  title="Edit topic order"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter(ALL_STATUS);
                    setEditTopics(
                      topicList.map((topic, index) => ({
                        ...topic,
                        sort_order: index + 1,
                      }))
                    );
                    canvasRef.current?.scrollTo({ top: 0 });
                  }}
                  icon={<ArrowDownUp size={18} />}
                >
                  Edit Order
                </Button>
                <Button
                  variant="primary"
                  icon={<Plus size={18} />}
                  onClick={openCreateTopic}
                >
                  Add Topic
                </Button>
              </>
            )}
          </>,
          actionsSlot
        )}

      <div className="library-browse">
        <aside className="library-topic-nav" aria-label="Status">
          <p className="library-topic-nav__title">Status</p>
          <FilterChips
            chips={filterChips}
            activeId={statusFilter}
            onChange={id => {
              if (isEditing) return;
              setStatusFilter(id);
              canvasRef.current?.scrollTo({ top: 0, behavior: "smooth" });
            }}
            ariaLabel="Filter topics by status"
            orientation="vertical"
          />
        </aside>

        <div className="library-browse__main">
          <div className="library-browse__toolbar">
            <LayoutToggle
              value={layout}
              onChange={setLayout}
              disabled={isEditing}
            />
          </div>
          <div className="admin-video-workspace__canvas" ref={canvasRef}>
            {renderCanvas()}
          </div>
        </div>
      </div>

      <CommonModal
        isOpen={!!topicForm}
        toggle={closeTopicForm}
        title={isEditMode ? "Edit Topic" : "Add Topic"}
        size="md"
        showHeader={true}
        footer={
          <>
            <Button variant="secondary" onClick={closeTopicForm}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={saving}
              disabled={!topicForm?.label.trim()}
              onClick={handleSubmitTopicForm}
            >
              {isEditMode ? "Save Changes" : "Create"}
            </Button>
          </>
        }
      >
        <CommonInput
          name="topic-label"
          label="Topic"
          value={topicForm?.label ?? ""}
          placeholder="e.g. Playbooks"
          onChange={e => setTopicFormLabel(e.target.value)}
          onKeyDown={e => e.key === "Enter" && handleSubmitTopicForm()}
          variant="field"
        />
      </CommonModal>

      <CommonModal
        isOpen={!!statusTarget}
        toggle={() => setStatusTarget(null)}
        title={statusTarget?.toActive ? "Activate Topic" : "Deactivate Topic"}
        size="md"
        showHeader={true}
        footer={
          <>
            <Button variant="secondary" onClick={() => setStatusTarget(null)}>
              Cancel
            </Button>
            <Button
              variant={statusTarget?.toActive ? "primary" : "danger"}
              loading={saving}
              onClick={handleConfirmStatus}
            >
              {statusTarget?.toActive ? "Activate" : "Deactivate"}
            </Button>
          </>
        }
      >
        <p className="text-sm text-gray-600">
          Are you sure you want to{" "}
          {statusTarget?.toActive ? "activate" : "deactivate"}{" "}
          <strong>&quot;{statusTarget?.topic.label}&quot;</strong>?{" "}
          {statusTarget?.toActive
            ? "Its videos will be visible to their assigned roles again."
            : "Its videos will be hidden from every assigned role until you activate it again."}
        </p>
      </CommonModal>

      <DeleteModal
        isOpen={!!deleteTarget}
        toggle={() => setDeleteTarget(null)}
        title="Delete Topic"
        entityName={deleteTarget ? `"${deleteTarget.label}"` : "this topic"}
        warningItems={[
          deleteTarget?.video_count
            ? `${deleteTarget.video_count} video(s) will become uncategorized. The videos are not deleted.`
            : "No videos use this topic.",
          "The tab will disappear from users' home pages.",
        ]}
        onConfirm={handleConfirmDelete}
        loading={saving}
      />
    </div>
  );
};

export default TopicsPanel;
